import { z } from 'zod';
import { deployParamValuesSchema } from './deployParam.zod';

const designDeviceMetricToVarIdMapSchema = z.object({
    varId: z.string(),
    metricName: z.string(),
    isWritable: z.boolean().default(false),
});

export type DesignDeviceMetricToVarIdMap = z.infer<typeof designDeviceMetricToVarIdMapSchema>;

export const linkedDesignVersionConfigSchema = z.object({
    sparkplugNodeExpose: z.object({
        designNameAsDeviceId: z.string(),
        vars: z.preprocess(v => {
            if (Array.isArray(v)) {
                const result: DesignDeviceMetricToVarIdMap[] = [];
                for (const item of v) {
                    if (typeof item === 'object' || item !== null) {
                        if ('varName' in item) {
                            const newItem: DesignDeviceMetricToVarIdMap = {
                                varId: String(item.varName),
                                metricName: String(item.metricName || ''),
                                isWritable: Boolean(item.isWritable || false),
                            };
                            result.push(newItem);
                        } else if ('varId' in item) {
                            result.push(item as DesignDeviceMetricToVarIdMap);
                        }
                    }
                }
                return result;
            }
            return [];
        }, z.array(designDeviceMetricToVarIdMapSchema)) as z.ZodType<DesignDeviceMetricToVarIdMap[]>,
    }).nullable().default(null),
}).default({});
export interface LinkedDesignVersionConfig extends z.infer<typeof linkedDesignVersionConfigSchema> {}

export const linkedDesignVersionDetailsSchema = z.object({
    id: z.string().uuid(),
    design: z.object({
        id: z.string().uuid(),
        name: z.string(),
    }),
    number: z.number(),
});

export const linkedDesignVersionSchema = z.object({
    config: linkedDesignVersionConfigSchema,
    designVersion: linkedDesignVersionDetailsSchema,
});

export type LinkedDesignVersion = z.infer<typeof linkedDesignVersionSchema>;

export const activeOperatorAuthMethodSchema = z.object({
    authType: z.enum([ 'badge', 'username_password' ]),
    access: z.enum([ 'enabled', 'disabled' ]),
});

export type ActiveOperatorAuthMethod = z.infer<typeof activeOperatorAuthMethodSchema>;

export const latestDeploymentDefaultsSchema = z.object({
    designVersionId: z.string().nullable().default(null),
    screenId: z.string().nullable().default(null),
}).optional();

type LatestDeploymentDefaults = z.infer<typeof latestDeploymentDefaultsSchema>;

export const activeOperatorSettingsSchema = z.object({
    enabled: z.boolean().default(false),
});

export interface ActiveOperatorSettings extends z.infer<typeof activeOperatorSettingsSchema> {}

export const deploymentConfigSchema = z.object({
    /**
     * if set, the deployment will be configured as a sparkplug node
     */
    deploymentSparkplugNode: z.object({
        version: z.literal('spBv1.0').default('spBv1.0'),
        /** defaults to context-name + runner-id */
        groupId: z.string().catch(''),
        /** defaults to deployment-name + deployment version*/
        nodeId: z.string().catch(''),
        usingSparkplug: z.boolean().default(true),
    }),
    deployParamVals: deployParamValuesSchema.default({}),
    defaults: z.preprocess(incomingSchema => {
        if (
            typeof incomingSchema === 'object' && incomingSchema !== null &&
            'screen' in incomingSchema && typeof incomingSchema.screen === 'string' &&
            'designVersionId' in incomingSchema && typeof incomingSchema.designVersionId === 'string'
        ) {
            const ret: LatestDeploymentDefaults = {
                screenId: incomingSchema.screen,
                designVersionId: incomingSchema.designVersionId,
            };
            return ret;
        }
        const ret: LatestDeploymentDefaults = {
            screenId: '',
            designVersionId: '',
        };
        return ret;
    }, latestDeploymentDefaultsSchema),
    activeOperatorAuthMethods: z.array(activeOperatorAuthMethodSchema).default([]),
    activeOperatorSettings: activeOperatorSettingsSchema.default({ enabled: false }),
    /**
     * When true, the runner will include CPU/heap resource samples in health updates.
     * Hidden in the UI unless debug mode is active.
     */
    enablePerformanceMetrics: z.boolean().default(false),
    /**
     * When enabled, overrides the default applet event rate limit for this deployment.
     * Controls how many varSync/appletEvent operations are allowed per time window
     * before the runtime terminates with a fatal error.
     */
    appletEventRateLimit: z.object({
        enabled: z.boolean()
            .default(true),
        qty: z.number()
            .int()
            .min(1)
            .default(50),
        ms: z.number()
            .int()
            .min(1)
            .default(1000),
    }).default({ enabled: true, qty: 50, ms: 1000 }),
});

export const deploymentSchema = z.object({
    id: z.string().uuid(),
    runnerId: z.string().min(1),
    contextId: z.string(),
    name: z.string().min(1),
    notes: z.string(),
    /**
     * The config schema version, should be changed every time there's breaking change in the config typedefs
     */
    schemaVersion: z.literal(1).default(1),
    config: deploymentConfigSchema,
    version: z.number(),
    linkedDesignVersions: z.array(linkedDesignVersionSchema),
    /** this is an advanced config option that we intentionally do not provide UI for */
    scriptRateLimit: z.object({
        qty: z.number(),
        ms: z.number(),
    }).optional(),
});

export interface Deployment extends z.infer<typeof deploymentSchema> {}
